import React, {useEffect, useMemo, useState} from 'react';
import {classes, style} from './doppe-action-type-media-gallery-item-preview.st.css';
import {
    cssEllipsis,
    flexColumn,
    flexFillRemainingHorizontalSpace,
    flexFillRemainingVerticalSpace,
    flexJustifyContentSpaceBetween,
    flexRowAlignToCenter, gridLayoutCenterAllChildren,
    gridLayoutStretchAllChildren,
    Icon,
    IconButton,
    ListViewDataSourceDataItem,
    reactCreateMemo,
    Swiper,
    useDynamicRef,
    useSwiperNavigation,
    useWindowDependantMemo
} from '@wix/devzai-utils-react';
import {
    bymoPageMediaIsBymoPageWixVideoResource,
    doppeHideableValueIsVisible,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemCalculateItemSections,
    useBymoPageProps
} from '../../../client-server-common';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppeViewerAppIconChevronUpMedium,
    DoppeViewerAppIconDismissMedium,
    DoppeViewerAppIconDismissSmall,
    DoppeViewerAppIconExitFullScreenMedium,
    DoppeViewerAppIconFullScreenMedium
} from '../../../components/doppe-viewer-icons/doppe-viewer-icons';
import {
    assertDefined,
    evaluateFunction,
    HtmlObjectHorizontalPosition,
    HtmlObjectResizeMode,
    HtmlObjectVerticalPosition,
    uiPartsGroupCreateMarking
} from '@wix/devzai-utils-common';
import {DoppePageViewerPartsGroups} from '../../../components/doppe-page-viewer-part/doppe-page-viewer-part';
import {
    DoppeViewerHorizontalButton
} from '../../../components/doppe-viewer-horizontal-button/doppe-viewer-horizontal-button';
import {DoppePageMediaViewMediaBackground} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    DoppeActionTypeWidgetContentLayoutNewSectionsAndButtons
} from '../../../components/doppe-action-type-widget-content-layout-new-sections-and-buttons/doppe-action-type-widget-content-layout-new-sections-and-buttons';
import {IDoppeViewerActionContext} from '../../../doppe-sdk';
import {DoppeActionGoalType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {CommonClasses} from '@wix/devzai-common-client';
import {BymoPageLayout, useBymoPageLayout} from '../../../components/bymo-page-style/bymo-page-style';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {doppeAppLayoutGap} from '@wix/doppe-ui';
import {
    useDoppViewerIsRtl
} from '../../../components/doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';
import IconChevronRight
    from '../../../components/doppe-action-button-templates/doppe-standard-action-button-template/icons/ChevronRight.svg';
import IconChevronLeft
    from '../../../components/doppe-action-button-templates/doppe-standard-action-button-template/icons/ChevronLeft.svg';
import {DoppeActionTypeMediaGallery} from '../../doppe-action-type-media-gallery/doppe-action-type-media-gallery';
import {MediaFeaturesDetector} from '@wix/devzai-utils-dom';
import {DoppeActionCtaButtonsSettings} from '../../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    DoppeActionContentSectionsSettings
} from '../../../client-server-common/types/doppe-action-content-sections-settings';
import {DoppeBymoPageMediaView} from '../../../components/doppe-bymo-page-media-view/doppe-bymo-page-media-view';

export const DoppeActionTypeMediaGalleryItemPreview = reactCreateMemo(function DoppeActionTypeMediaGalleryItemPreview(
    props: React.HTMLAttributes<any> & {
        dismissButtonCallback: () => void;
        actionContext: IDoppeViewerActionContext<DoppeActionTypeMediaGallery.ActionSettings>;
        reportGoalAchieved: (itemId: string, goalType: DoppeActionGoalType) => void;
        galleryListItemsDataSource: ListViewDataSourceDataItem<DoppeActionTypeMediaGallery.MediaGalleryListItemSettings>[];
        currentItemId: string;
        listItemTemplateCtaButtonsSettings?: DoppeActionCtaButtonsSettings;
        listItemTemplateSectionsSettings?: DoppeActionContentSectionsSettings;
        previewShowDescription?: boolean;
    }
) {
    const {
        currentItemId,
        galleryListItemsDataSource,
        actionContext,
        dismissButtonCallback,
        reportGoalAchieved,
        listItemTemplateCtaButtonsSettings = {
            ctaButtons: []
        },
        listItemTemplateSectionsSettings = {
            sections: []
        },
        previewShowDescription = true,
        className
    } = props;

    const swiperRef = useDynamicRef<HTMLDivElement>();
    const swiper = useSwiperNavigation(swiperRef);

    const initialItemIndex = useMemo(() => {
        return galleryListItemsDataSource.findIndex(item => item.id === currentItemId)
    }, [galleryListItemsDataSource, currentItemId]);

    const currentItemIndex = useMemo(() => {
        return swiper.currentSlide !== undefined ? swiper.currentSlide : initialItemIndex
    }, [swiper.currentSlide, initialItemIndex]);

    const listItemSettings = useMemo(() => {
        const item = assertDefined(galleryListItemsDataSource[currentItemIndex >= 0 ? currentItemIndex : 0]);
        return item.dataItem;
    }, [currentItemIndex, galleryListItemsDataSource]);


    useBymoPageProps();


    const {
        ribbon,
        description,
        title,
    } = listItemSettings;

    const sections = doppeListActionListItemCalculateItemSections(listItemTemplateSectionsSettings, listItemSettings).sections.filter(section => section.isVisible);
    const ctaButtons = doppeListActionListItemCalculateItemButtons(listItemTemplateCtaButtonsSettings, listItemSettings).ctaButtons.filter(button => button.isVisible);


    const [isDescriptionToggled, setIsDescriptionToggled] = useState<boolean | null>(null);

    const bymoPageLayout = useBymoPageLayout();
    const isMobile = bymoPageLayout === BymoPageLayout.Narrow;

    const hasContent = sections.length > 0 || ctaButtons.length > 0 || (previewShowDescription && description && doppeHideableValueIsVisible(description));

    const isDescriptionPreviewOn = useMemo(() => {
        return (isDescriptionToggled === null ? !isMobile : isDescriptionToggled) && hasContent;
    }, [isDescriptionToggled, isMobile, hasContent]);

    useEffect(() => {
        if (isDescriptionToggled && !hasContent) {
            setIsDescriptionToggled(false);
        }
    }, [currentItemIndex, hasContent, isDescriptionToggled]);


    const isRtl = useDoppViewerIsRtl();


    const openIcon = !isMobile ? DoppeViewerAppIconExitFullScreenMedium : DoppeViewerAppIconChevronUpMedium;
    const closeIcon = !isMobile ? DoppeViewerAppIconFullScreenMedium : DoppeViewerAppIconDismissSmall;

    const supportsHover = useWindowDependantMemo(() => MediaFeaturesDetector.supportsHover, []) ?? true

    return (
        <div
            className={style(classes.root, {
                isContentToggled: isDescriptionPreviewOn
            }, className)}
        >
            <div
                className={style(classes.detailsSection, {}, flexRowAlignToCenter(), flexJustifyContentSpaceBetween())}
            >
                <div
                    className={style(classes.titleContainer, {}, flexRowAlignToCenter(), doppeAppLayoutGap(18))}
                >
                    {
                        !isMobile && hasContent ?
                            <DoppeViewerHorizontalButton
                                className={style(classes.fullScreenIcon, {})}
                                prefixIcon={isDescriptionPreviewOn ? DoppeViewerAppIconFullScreenMedium : DoppeViewerAppIconExitFullScreenMedium}
                                textual={true}
                                onClick={() => {
                                    setIsDescriptionToggled(!isDescriptionPreviewOn);
                                }}
                            />
                            :
                            null
                    }
                    {
                        ribbon && doppeHideableValueIsVisible(ribbon) ? <div
                                className={style(classes.ribbon, {})}
                            >
                                {ribbon}
                            </div>
                            :
                            null
                    }
                </div>
                <div
                    className={style(classes.slidesCounter, {})}
                >
                    {`${(swiper.currentSlide ?? 0) + 1} / ${swiper.slidesCount ?? 1}`}
                </div>
                <DoppeViewerHorizontalButton
                    className={style(classes.closeButton, {})}
                    prefixIcon={DoppeViewerAppIconDismissMedium}
                    textual={true}
                    {...uiPartsGroupCreateMarking(DoppePageViewerPartsGroups.CloseButton)}
                    onClick={() => {
                        dismissButtonCallback();
                    }}
                />
            </div>
            <div
                className={style(classes.itemContainer, {}, gridLayoutStretchAllChildren())}
            >
                <Swiper
                    ref={swiperRef}
                    visibleItemsCount={1}
                    className={style(classes.swiper, {})}
                    snapToItemIndex={initialItemIndex}
                    isRtl={isRtl}
                >
                    {
                        galleryListItemsDataSource.map((dataSourceItem) => {

                            const media = dataSourceItem.dataItem.media;
                            const title = dataSourceItem.dataItem.title;

                            return <div
                                className={style(classes.slide, {}, gridLayoutCenterAllChildren())}
                                key={dataSourceItem.id}
                                onClick={() => {
                                }}
                            >
                                <DoppeBymoPageMediaView
                                    className={style(classes.media)}
                                    mediaResource={media && bymoPageMediaIsBymoPageWixVideoResource(media) ? {
                                        ...media,
                                        renderAsPlayer: true,
                                        playerAutoPlay: false,
                                    } : media}
                                    widthSpec={{precise: true}}
                                    layoutSpec={{autoHeight: true}}
                                    mediaVerticalPosition={HtmlObjectVerticalPosition.Center}
                                    mediaHorizontalPosition={HtmlObjectHorizontalPosition.Center}
                                    mediaResizeMode={HtmlObjectResizeMode.Contain}
                                    mediaBackground={DoppePageMediaViewMediaBackground.Transparent}
                                />
                                {
                                    !isMobile && !isDescriptionPreviewOn ?
                                        <DoppeActionTypeWidgetInnerTitle
                                            className={style(classes.desktopTitleInline, {}, cssEllipsis())}
                                        >
                                            {title}
                                        </DoppeActionTypeWidgetInnerTitle>
                                        :
                                        null
                                }
                            </div>
                        })
                    }
                </Swiper>
                {
                    supportsHover && swiper.slidesCount > 1 ?
                        <>
                            <IconButton
                                className={style(classes.arrowScrollButton, {
                                    isDisabled: swiper.currentSlide === 0
                                }, classes.leftEdge)}
                                iconClassName={style(classes.arrowScrollButtonIconLeft, {})}
                                icon={isRtl ? IconChevronRight : IconChevronLeft}
                                onClick={() => {
                                    if (swiper.currentSlide !== undefined) {
                                        const targetElement = swiperRef.current?.children?.item(Math.max(swiper.currentSlide - 1, 0));

                                        if (targetElement) {
                                            swiperRef.current?.scrollTo({
                                                left: evaluateFunction(() => {
                                                    const previousSibling = targetElement.previousElementSibling as HTMLElement | null;
                                                    if (previousSibling) {
                                                        return previousSibling.offsetLeft + previousSibling.offsetWidth * (isRtl ? -1 : 1);
                                                    } else {
                                                        return 0;
                                                    }
                                                }),
                                                behavior: 'smooth'
                                            })
                                        }
                                    }

                                }}
                            />
                            <IconButton
                                className={style(classes.arrowScrollButton, {
                                    isDisabled: swiper.currentSlide === swiper.slidesCount - 1
                                }, classes.rightEdge)}
                                iconClassName={style(classes.arrowScrollButtonIconRight, {})}
                                icon={!isRtl ? IconChevronRight : IconChevronLeft}
                                onClick={() => {
                                    if (swiper.currentSlide !== undefined) {
                                        const targetElement = swiperRef.current?.children?.item(Math.min(swiper.currentSlide + 1, swiper.slidesCount - 1));

                                        if (targetElement) {
                                            swiperRef.current?.scrollTo({
                                                left: evaluateFunction(() => {
                                                    const previousSibling = targetElement.previousElementSibling as HTMLElement | null;
                                                    if (previousSibling) {
                                                        return previousSibling.offsetLeft + previousSibling.offsetWidth * (isRtl ? -1 : 1);
                                                    } else {
                                                        return 0;
                                                    }
                                                }),
                                                behavior: 'smooth'
                                            })
                                        }
                                    }

                                }}
                            />
                        </>
                        :
                        null
                }
            </div>
            {
                isMobile && !isDescriptionPreviewOn ?
                    <div
                        className={style(classes.closedCardHeader, {}, flexRowAlignToCenter(), doppeAppLayoutGap(12), hasContent ? CommonClasses.button : undefined)}
                        onClick={() => {
                            if (hasContent) {
                                setIsDescriptionToggled(!isDescriptionPreviewOn);
                            }
                        }}
                    >
                        <DoppeActionTypeWidgetInnerTitle
                            className={style(classes.cardHeaderTitle, {}, flexFillRemainingHorizontalSpace(), cssEllipsis())}
                        >
                            {title}
                        </DoppeActionTypeWidgetInnerTitle>
                        {
                            hasContent ?
                                <Icon
                                    className={style(classes.cardHeaderIcon, {})}
                                    icon={isDescriptionPreviewOn ? closeIcon : openIcon}
                                />
                                :
                                null
                        }
                    </div>
                    :
                    isDescriptionPreviewOn ?
                        <div
                            className={style(classes.card, {}, flexColumn())}
                        >
                            <div
                                className={style(classes.openCardHeader, {}, flexRowAlignToCenter(), doppeAppLayoutGap(12), CommonClasses.button)}
                                onClick={() => {
                                    setIsDescriptionToggled(!isDescriptionPreviewOn);
                                }}
                            >
                                <DoppeActionTypeWidgetInnerTitle
                                    className={style(classes.cardHeaderMainTitle, {}, flexFillRemainingHorizontalSpace(), cssEllipsis())}
                                >
                                    {title}
                                </DoppeActionTypeWidgetInnerTitle>
                                {
                                    isMobile ?
                                        <Icon
                                            className={style(classes.cardHeaderIcon, {})}
                                            icon={isDescriptionPreviewOn ? closeIcon : openIcon}
                                        />
                                        :
                                        null
                                }
                            </div>
                            <DoppeActionTypeWidgetContentLayoutNewSectionsAndButtons
                                className={style(classes.cardContent, {}, flexFillRemainingVerticalSpace())}
                                isFixedHeight={true}
                                sections={sections}
                                buttons={ctaButtons}
                                actionContext={actionContext}
                                reportGoalAchieved={(goalType) => {
                                    reportGoalAchieved(currentItemId, goalType);
                                }}
                                maxButtonsLimitForFixedFooter={isMobile ? 1 : 4}
                                noContentTopPadding={true}
                            >
                                {
                                    previewShowDescription ?
                                        <DoppeActionTypeWidgetInnerDescription
                                            className={style(classes.cardHeaderMainDescription, {})}
                                            maxLines={Infinity}
                                        >
                                            {description}
                                        </DoppeActionTypeWidgetInnerDescription>
                                        :
                                        null
                                }
                            </DoppeActionTypeWidgetContentLayoutNewSectionsAndButtons>
                        </div>
                        :
                        null
            }
        </div>
    );
});

